import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  dataBuilderStore,
  selectCompanyMapperGateStatus,
  selectConfigByInstance,
  selectDatasetInstances,
  selectDeliverableClient,
  selectDeliverableName,
  selectLagData,
  selectModelVersions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import type { BuildVerifyPayloadResult } from './build-verify-payload';
import { estimateDataBuilderPayload } from './data-builder-api';
import type { RowLimitResultsByInstanceId } from './data-builder-row-limit.types';
import { extractDataBuilderRowLimitErrorMessage } from './data-builder-row-limit-errors';
import { parseValidateRowLimitResponse } from './parse-validate-row-limit-response';
import { getReadyInstanceIdsForRowLimit } from './row-limit-readiness';
import { useBuildDataBuilderPayload } from './useBuildDataBuilderPayload';
import {
  useDataBuilderQuery,
  useDataBuilderSchema,
} from './useDataBuilderQuery';

const FALLBACK_NAME = 'Validating Row Limit';
const ROW_LIMIT_VALIDATION_DEBOUNCE_MS = 400;
const isDeliveryFormat = (
  value: unknown
): value is 'parquet' | 'csv' | 'excel' =>
  value === 'parquet' || value === 'csv' || value === 'excel';

type UseDataBuilderRowLimitResult = {
  resultsByInstanceId: RowLimitResultsByInstanceId;
  hasOverLimit: boolean;
  pendingInstanceIds: Set<string>;
  apiErrorInstanceIds: Set<string>;
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  isReady: boolean;
  isWaitingForCompanies: boolean;
};

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
};

// Rewrite deliverable names to instance ids so the `/estimate` response can
// round-trip back to instance ids without relying on user-editable labels.
const withInstanceIdProbeNames = (
  payloadResult: BuildVerifyPayloadResult
): BuildVerifyPayloadResult => {
  const instanceIds = Object.keys(payloadResult.deliverableNameByInstanceId);
  return {
    payload: {
      ...payloadResult.payload,
      deliverables: payloadResult.payload.deliverables.map(
        (deliverable, index) => {
          const instanceId = instanceIds[index];
          if (instanceId === undefined) {
            return deliverable;
          }
          return { ...deliverable, name: instanceId };
        }
      ),
    },
    deliverableNameByInstanceId: Object.fromEntries(
      instanceIds.map((id) => [id, id])
    ),
  };
};

export const useDataBuilderRowLimit = (): UseDataBuilderRowLimitResult => {
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useDataBuilderSchema();

  const companySelectionGateStatus = useDataBuilderSelector(
    selectCompanyMapperGateStatus
  );
  const datasetInstances = useDataBuilderSelector(selectDatasetInstances);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const lagData = useDataBuilderSelector(selectLagData);
  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);

  const trimmedName = storedName?.trim() ?? '';
  const payloadName = trimmedName.length > 0 ? trimmedName : FALLBACK_NAME;
  const payloadClient = storedClient !== '' ? storedClient : null;

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
    rowLimitValidationProbe: true,
  });

  const probe = useMemo(() => {
    if (rawSchema === undefined) {
      return null;
    }
    const labelMap = buildDatasetLabelMap(data?.datasets);
    const instanceLabelMap = buildDatasetInstanceLabelMap(
      datasetInstances,
      labelMap
    );
    const readyInstanceIds = getReadyInstanceIdsForRowLimit({
      datasetInstances,
      configByInstance,
      deliverablesConfig: data?.deliverablesConfig ?? {},
      modelVersions,
      instanceLabelById: instanceLabelMap,
    });
    if (readyInstanceIds.size === 0) {
      return null;
    }
    const built = build(rawSchema, readyInstanceIds);
    if (built === null) {
      return null;
    }
    const { payload, deliverableNameByInstanceId } =
      withInstanceIdProbeNames(built);
    if (payload.deliverables.length === 0) {
      return null;
    }
    const nameToInstanceId = Object.fromEntries(
      Object.entries(deliverableNameByInstanceId).map(([id, name]) => [
        name,
        id,
      ])
    );
    // Fold lagData into each instance's signature so changes to the global
    // lag value invalidate every instance and trigger a re-estimate.
    const signatureByInstanceId = Object.fromEntries(
      payload.deliverables.flatMap((deliverable) => {
        const instanceId = nameToInstanceId[deliverable.name];
        if (instanceId === undefined) {
          return [];
        }
        const signature = JSON.stringify({ deliverable, lagData });
        return [[instanceId, signature]];
      })
    );
    return {
      payload,
      nameToInstanceId,
      readyInstanceIds,
      signatureByInstanceId,
    };
  }, [
    build,
    configByInstance,
    data,
    datasetInstances,
    lagData,
    modelVersions,
    rawSchema,
  ]);

  const isWaitingForCompanies = companySelectionGateStatus === 'pending';
  const [resultsByInstanceId, setResultsByInstanceId] =
    useState<RowLimitResultsByInstanceId>({});
  const [pendingInstanceIds, setPendingInstanceIds] = useState<Set<string>>(
    new Set()
  );
  const [apiErrorInstanceIds, setApiErrorInstanceIds] = useState<Set<string>>(
    new Set()
  );
  const [requestError, setRequestError] = useState<unknown>(null);
  const requestSequenceRef = useRef(0);
  const latestSequenceByInstanceIdRef = useRef<Record<string, number>>({});
  const lastSignatureByInstanceIdRef = useRef<Record<string, string>>({});

  const changedInstanceIds = (() => {
    if (probe === null) {
      return [] as string[];
    }
    const previousSignatures = lastSignatureByInstanceIdRef.current;
    return Object.entries(probe.signatureByInstanceId)
      .filter(
        ([instanceId, signature]) =>
          previousSignatures[instanceId] !== signature
      )
      .map(([instanceId]) => instanceId);
  })();
  const changedInstanceIdsSignature = useMemo(
    () => JSON.stringify(changedInstanceIds),
    [changedInstanceIds]
  );
  const debouncedChangedInstanceIdsSignature = useDebouncedValue(
    changedInstanceIdsSignature,
    ROW_LIMIT_VALIDATION_DEBOUNCE_MS
  );
  const changedInstanceIdsStable =
    debouncedChangedInstanceIdsSignature === changedInstanceIdsSignature;
  const debouncedChangedInstanceIds = useMemo(() => {
    const parsed: unknown = JSON.parse(debouncedChangedInstanceIdsSignature);
    if (!Array.isArray(parsed)) {
      return [] as string[];
    }
    return parsed.filter((value): value is string => typeof value === 'string');
  }, [debouncedChangedInstanceIdsSignature]);

  const {
    mutate: estimateRowLimit,
    isError: isEstimateError,
    error: estimateError,
    reset: resetEstimateMutation,
  } = useMutation({
    mutationKey: ['data-builder-row-limit-estimate'],
    mutationFn: estimateDataBuilderPayload,
  });

  useEffect(() => {
    if (probe === null) {
      setResultsByInstanceId({});
      setPendingInstanceIds(new Set());
      setApiErrorInstanceIds(new Set());
      lastSignatureByInstanceIdRef.current = {};
      latestSequenceByInstanceIdRef.current = {};
      setRequestError(null);
      return;
    }

    const readyIds = probe.readyInstanceIds;
    setResultsByInstanceId((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([instanceId]) =>
          readyIds.has(instanceId)
        )
      )
    );
    setPendingInstanceIds((previous) => {
      const next = new Set(
        Array.from(previous).filter((instanceId) => readyIds.has(instanceId))
      );
      return next;
    });
    setApiErrorInstanceIds((previous) => {
      const next = new Set(
        Array.from(previous).filter((instanceId) => readyIds.has(instanceId))
      );
      return next;
    });
    lastSignatureByInstanceIdRef.current = Object.fromEntries(
      Object.entries(lastSignatureByInstanceIdRef.current).filter(
        ([instanceId]) => readyIds.has(instanceId)
      )
    );
    latestSequenceByInstanceIdRef.current = Object.fromEntries(
      Object.entries(latestSequenceByInstanceIdRef.current).filter(
        ([instanceId]) => readyIds.has(instanceId)
      )
    );
  }, [probe]);

  useEffect(() => {
    if (
      probe === null ||
      isWaitingForCompanies ||
      !changedInstanceIdsStable ||
      debouncedChangedInstanceIds.length === 0
    ) {
      return;
    }
    const changedSet = new Set(debouncedChangedInstanceIds);
    const deliverables = probe.payload.deliverables.filter((deliverable) => {
      const instanceId = probe.nameToInstanceId[deliverable.name];
      return instanceId !== undefined && changedSet.has(instanceId);
    });
    if (deliverables.length === 0) {
      return;
    }
    const requestPayload = {
      ...probe.payload,
      deliverables,
    };
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    for (const instanceId of changedSet) {
      latestSequenceByInstanceIdRef.current[instanceId] = requestSequence;
    }
    resetEstimateMutation();
    setRequestError(null);
    setPendingInstanceIds((previous) => {
      const next = new Set(previous);
      for (const instanceId of changedSet) {
        next.add(instanceId);
      }
      return next;
    });

    estimateRowLimit(requestPayload, {
      onSuccess: (response) => {
        const liveInstanceIds = new Set(
          Array.from(changedSet).filter(
            (instanceId) =>
              latestSequenceByInstanceIdRef.current[instanceId] ===
              requestSequence
          )
        );
        if (liveInstanceIds.size === 0) {
          return;
        }
        const responseDefaultFormat =
          response !== null &&
          typeof response === 'object' &&
          'default_format' in response &&
          isDeliveryFormat(response.default_format)
            ? response.default_format
            : null;
        if (responseDefaultFormat !== null) {
          dataBuilderStore.actions.setEstimateDefaultFormat(
            responseDefaultFormat
          );
        }
        const parsedResults = parseValidateRowLimitResponse(
          response,
          probe.nameToInstanceId
        );
        setResultsByInstanceId((previous) => {
          const next = { ...previous };
          for (const instanceId of liveInstanceIds) {
            delete next[instanceId];
          }
          for (const [instanceId, result] of Object.entries(parsedResults)) {
            if (
              liveInstanceIds.has(instanceId) &&
              probe.readyInstanceIds.has(instanceId)
            ) {
              next[instanceId] = result;
            }
          }
          return next;
        });
        const nextSignatures = { ...lastSignatureByInstanceIdRef.current };
        for (const instanceId of liveInstanceIds) {
          const signature = probe.signatureByInstanceId[instanceId];
          if (signature !== undefined) {
            nextSignatures[instanceId] = signature;
          }
        }
        lastSignatureByInstanceIdRef.current = nextSignatures;
        setPendingInstanceIds((previous) => {
          const next = new Set(previous);
          for (const instanceId of liveInstanceIds) {
            next.delete(instanceId);
          }
          return next;
        });
        setApiErrorInstanceIds((previous) => {
          const next = new Set(previous);
          for (const instanceId of liveInstanceIds) {
            next.delete(instanceId);
          }
          return next;
        });
      },
      onError: (error) => {
        const liveInstanceIds = new Set(
          Array.from(changedSet).filter(
            (instanceId) =>
              latestSequenceByInstanceIdRef.current[instanceId] ===
              requestSequence
          )
        );
        if (liveInstanceIds.size === 0) {
          return;
        }
        setRequestError(error);
        setResultsByInstanceId((previous) => {
          const next = { ...previous };
          for (const instanceId of liveInstanceIds) {
            delete next[instanceId];
          }
          return next;
        });
        setPendingInstanceIds((previous) => {
          const next = new Set(previous);
          for (const instanceId of liveInstanceIds) {
            next.delete(instanceId);
          }
          return next;
        });
        setApiErrorInstanceIds((previous) => {
          const next = new Set(previous);
          for (const instanceId of liveInstanceIds) {
            next.add(instanceId);
          }
          return next;
        });
      },
    });
  }, [
    changedInstanceIdsStable,
    debouncedChangedInstanceIds,
    estimateRowLimit,
    isWaitingForCompanies,
    probe,
    resetEstimateMutation,
  ]);

  const hasOverLimit = Object.values(resultsByInstanceId).some(
    (result) => result.severity === 'error'
  );

  const rowLimitError = (() => {
    if (requestError instanceof Error) {
      return requestError;
    }
    if (isEstimateError && estimateError instanceof Error) {
      return estimateError;
    }
    return null;
  })();
  const errorMessage = extractDataBuilderRowLimitErrorMessage(rowLimitError);
  const canRunRowLimitProbe = probe !== null;
  const isPending = pendingInstanceIds.size > 0;
  const isError = requestError !== null || isEstimateError;
  const isReady =
    isWaitingForCompanies || !canRunRowLimitProbe || (!isPending && !isError);

  return {
    resultsByInstanceId,
    hasOverLimit,
    pendingInstanceIds,
    apiErrorInstanceIds,
    isPending,
    isError,
    errorMessage,
    isReady,
    isWaitingForCompanies,
  };
};
